import axios from 'axios';
import type { InternalAxiosRequestConfig } from 'axios';
import { clearAuthData, refreshToken, getAccessToken, getCsrfToken, isTokenExpired, setAccessToken, setCsrfToken } from './authService';
import config from '../config/config';
import securityConfig from '../config/securityConfig';
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  withCredentials: true,
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  },
  timeout: 10000
});

// List of public endpoints that don't need authentication
const publicEndpoints = [
  '/feed/filtered',
  '/feed/search',
  '/auth/login',
  '/auth/register',
  '/auth/refresh'
];

// Helper function to clear all auth cookies
const clearAuthCookies = () => {
  // Clear cookies with different path and domain combinations
  const cookiesToClear = ['auth_token', 'refresh_token', 'JSESSIONID'];
  const paths = ['/', '/api'];
  
  cookiesToClear.forEach(cookieName => {
    // Clear cookie with no specific domain
    paths.forEach(path => {
      document.cookie = `${cookieName}=; Path=${path}; Expires=Thu, 01 Jan 1970 00:00:01 GMT; SameSite=Strict;`;
    });
    
    // Clear cookie with current domain
    const domain = window.location.hostname;
    paths.forEach(path => {
      document.cookie = `${cookieName}=; Path=${path}; Domain=${domain}; Expires=Thu, 01 Jan 1970 00:00:01 GMT; SameSite=Strict;`;
    });
    
    // Clear cookie with .domain.com (if applicable)
    if (domain.includes('.')) {
      const rootDomain = domain.split('.').slice(-2).join('.');
      paths.forEach(path => {
        document.cookie = `${cookieName}=; Path=${path}; Domain=.${rootDomain}; Expires=Thu, 01 Jan 1970 00:00:01 GMT; SameSite=Strict;`;
      });
    }
  });
};

// Add a flag to track if we're currently in a refresh cycle
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;
let lastRefreshAttempt = 0;
const MIN_REFRESH_INTERVAL = 10000; // 10 seconds

// Request interceptor
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {  
    const isPublicEndpoint = publicEndpoints.some(endpoint => 
      config.url?.includes(endpoint)
    );

    // Always include credentials for cookies
    config.withCredentials = true;

    // Get token from memory
    const token = getAccessToken();
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add CSRF token for sensitive operations
    if (config.method?.toLowerCase() !== 'get' && !isPublicEndpoint) {
      // Get CSRF token
      const csrfToken = getCsrfToken();
      if (csrfToken) {
        config.headers[securityConfig.csrfHeaderName] = csrfToken;
      }
    }
    
    // Add CORS headers if needed
    if (window.location.hostname.includes('wanttd.com')) {
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    // Extract CSRF token from various possible sources
    let csrfToken = null;
    
    // 1. Check response headers (lowercase header name)
    const headerName = securityConfig.csrfHeaderName.toLowerCase();
    if (response.headers[headerName]) {
      csrfToken = response.headers[headerName];
      console.log('Found CSRF token in response headers');
    }
    
    // 2. Check response body if it's an auth response
    if (!csrfToken && 
        response.data && 
        typeof response.data === 'object' && 
        response.data.csrfToken) {
      csrfToken = response.data.csrfToken;
      console.log('Found CSRF token in response body');
    }
    
    // 3. Try to extract from cookies
    if (!csrfToken) {
      try {
        const cookieValue = document.cookie
          .split('; ')
          .find(row => row.startsWith(`${securityConfig.cookies.csrfCookie}=`))
          ?.split('=')[1];
        
        if (cookieValue) {
          csrfToken = cookieValue;
          console.log('Found CSRF token in cookies');
        }
      } catch (error) {
        console.error('Error extracting CSRF token from cookies:', error);
      }
    }
    
    // Store the token if we found one
    if (csrfToken) {
      setCsrfToken(csrfToken);
      console.log('CSRF token updated');
    }
    
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Prevent infinite loops - if this is already a retry, don't try again
    if (originalRequest._retry) {
      return Promise.reject(error);
    }
    
    // Check if the error is a 401 (Unauthorized)
    if (error.response?.status === 401) {
      // Check if token expired header is present or if token is expired
      const tokenExpired = error.response.headers['token-expired'] === 'true' || 
                          isTokenExpired(getAccessToken() || '');
      
      if (tokenExpired) {
        originalRequest._retry = true;
        
        // Prevent too frequent refresh attempts
        const now = Date.now();
        if (now - lastRefreshAttempt < MIN_REFRESH_INTERVAL) {
          console.log(`Too many refresh attempts. Waiting...`);
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // If we still have a valid token, use it
          const currentToken = getAccessToken();
          if (currentToken && !isTokenExpired(currentToken)) {
            originalRequest.headers.Authorization = `Bearer ${currentToken}`;
            return axios(originalRequest);
          }
          
          // Otherwise, redirect to login
          clearAuthData();
          if (window.location.pathname !== '/signin' && window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          return Promise.reject(error);
        }
        
        lastRefreshAttempt = now;
        
        // Check if we're already refreshing
        if (isRefreshing) {
          try {
            // Wait for the existing refresh to complete
            const token = await refreshPromise;
            if (token) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return axios(originalRequest);
            }
            return Promise.reject(error);
          } catch (err) {
            return Promise.reject(err);
          }
        }
        
        // Start refreshing
        isRefreshing = true;
        refreshPromise = refreshToken();
        
        try {
          const newToken = await refreshPromise;
          
          if (newToken) {
            // Update the original request with the new token
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            
            // If request failed due to missing CSRF token, add it now
            const csrfToken = getCsrfToken();
            if (csrfToken && originalRequest.method?.toLowerCase() !== 'get') {
              originalRequest.headers[securityConfig.csrfHeaderName] = csrfToken;
            }
            
            return axios(originalRequest);
          } else {
            // If refresh failed, redirect to login
            clearAuthData();
            
            // Redirect to login page - but only do this once!
            if (window.location.pathname !== '/signin' && window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
            
            return Promise.reject(error);
          }
        } catch (refreshError) {
          // Clear tokens
          clearAuthData();
          
          // Redirect to login page - but only do this once!
          if (window.location.pathname !== '/signin' && window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
          refreshPromise = null;
        }
      }
    }
    
    return Promise.reject(error);
  }
);

export default api;

export interface WillFeedParams {
  category?: string;
  location?: string;
  maxBudget?: number;
  cursor?: string;
  size?: number;
  searchTerm?: string;
}

export interface Will {
  media: any[];
  id: string;
  title: string;
  description: string;
  budget: number | null;
  currency: string | null;
  fulfillmentTime: string | null;
  category: string;
  location: string;
  wanterId: string;
  wanterName: string;
  wanterProfilePicture?: string | null;
  createdAt: string;
  status: string;
  offerCount: number;
  likeCount: number;
  commentCount: number;
  images: string[];
  liked: boolean;
  primaryImageUrl: string | null;
  formattedAmount: string;
}

interface WillResponse {
  content: Will[];
  totalPages: number;
  totalElements: number;
  last: boolean;
  numberOfElements: number;
  first: boolean;
  empty: boolean;
}

export const getFilteredWills = async (params: WillFeedParams): Promise<WillResponse> => {
  const response = await api.get<WillResponse>('/feed/filtered', { 
    params: {
      ...params,
      searchTerm: params.searchTerm?.toLowerCase()
    }
  });
  return response.data;
};

interface SearchResponse {
  content: Will[];
  hasMore: boolean;
}

const searchCache = new Map<string, SearchResponse>();

export const searchWills = async (
  searchTerm: string,
  cursor?: string | null
): Promise<SearchResponse> => {
  const cacheKey = `${searchTerm}-${cursor || ''}`;
  
  // Check cache first
  if (searchCache.has(cacheKey)) {
    return searchCache.get(cacheKey)!;
  }

  const params = new URLSearchParams({
    searchTerm,
    size: '10',
  });

  if (cursor) {
    params.append('cursor', cursor);
  }

  try {
    const response = await api.get<SearchResponse>(`/feed/search?${params.toString()}`);
    
    // Cache the results
    searchCache.set(cacheKey, response.data);
    
    // Clear old cache entries if cache gets too large
    if (searchCache.size > 100) {
      const firstKey = searchCache.keys().next().value;
      searchCache.delete(firstKey);
    }
    
    return response.data;
  } catch (error) {
    throw new Error('Search request failed');
  }
};

export const createWillWithMedia = async (willData: any, mediaFiles: File[]) => {
  try {
    const formData = new FormData();
    formData.append('will', JSON.stringify(willData));
    
    // Append each file to the form data as 'files' part
    mediaFiles.forEach(file => {
      formData.append('files', file);
    });

    const response = await api.post('/wills', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('Error creating will with media:', error);
    throw error;
  }
};

export const updateWillWithMedia = async (willId: string, willData: any, mediaFiles: File[]) => {
  try {
    const formData = new FormData();
    formData.append('will', JSON.stringify(willData));
    
    // Append each file to the form data
    mediaFiles.forEach(file => {
      formData.append('files', file);
    });
    
    const response = await api.put(`/wills/${willId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('Error updating will with media:', error);
    throw error;
  }
};

export const createOfferWithMedia = async (willId: string, offerData: any, mediaFiles: File[]) => {
  try {
    const formData = new FormData();
    formData.append('offer', JSON.stringify(offerData));
    
    // Append each file to the form data
    mediaFiles.forEach(file => {
      formData.append('files', file);
    });
    
    const response = await api.post(`/offers/will/${willId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('Error creating offer with media:', error);
    throw error;
  }
};

export const updateOfferWithMedia = async (offerId: string, offerData: any, mediaFiles: File[]) => {
  try {
    const formData = new FormData();
    formData.append('offer', JSON.stringify(offerData));
    
    // Append each file to the form data
    mediaFiles.forEach(file => {
      formData.append('files', file);
    });
    
    const response = await api.put(`/offers/${offerId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('Error updating offer with media:', error);
    throw error;
  }
};

// Get AI suggestion for offer
export const getOfferAiSuggestion = async (willId: string) => {
  const token = getAccessToken();
  
  try {
    const response = await fetch(`${config.apiUrl}/offers/will/${willId}/ai-suggestion`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to get AI suggestion');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error getting AI suggestion:', error);
    throw error;
  }
};

// Regenerate AI suggestion
export const regenerateOfferAiSuggestion = async (willId: string) => {
  const token = getAccessToken();
  
  try {
    const response = await fetch(`${config.apiUrl}/offers/will/${willId}/ai-suggestion/regenerate`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({})
    });
    
    if (!response.ok) {
      throw new Error('Failed to regenerate AI suggestion');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error regenerating AI suggestion:', error);
    throw error;
  }
};
